import {ImplicitConicTool2D} from './ImplicitConicTool2D';
import {ImplicitParametricCurveIntersector2D} from './ImplicitParametricCurveIntersector2D';
import {ParametricSplineCurveAdaptor2D} from './ParametricSplineCurveAdaptor2D';

/**
 * Puppy CAD 内核 语义二维直线—Bezier/B-Spline 求交门面。
 *
 * 主算法严格使用 `IntImpParGen` 风格的 C1 分段、原生参数采样、FunctionAllRoots、
 * transition 与容差 segment，不再使用 Bernstein 多项式或逐 Bezier span 求根。
 */
export class LineSplineIntersectionSolver {
    /** Puppy CAD 内核 等价隐式—参数曲线求交器。 */
    private readonly intersector: ImplicitParametricCurveIntersector2D;

    /**
     * 创建直线—样条求交门面。
     * @param intersector Puppy CAD 内核 等价公共求交器。
     */
    public constructor(
        intersector: ImplicitParametricCurveIntersector2D
            = new ImplicitParametricCurveIntersector2D(),
    ) {
        this.intersector = intersector;
    }

    /**
     * 求解无限支撑直线与样条。
     * @param line 直线定义。
     * @param curve 参数样条定义。
     * @param options Puppy CAD 内核 双容差选项。
     * @returns 离散交点和容差重合段。
     */
    public solveSupportingLine(
        line: LineSplineIntersectionLine2D,
        curve: LineSplineCurveDefinition2D,
        options?: LineSplineIntersectionOptions,
    ): LineSplineIntersectionResult2D {
        return this.solve(line, curve, false, options);
    }

    /**
     * 求解有限线段与样条。
     * @param line 有限线段定义。
     * @param curve 参数样条定义。
     * @param options Puppy CAD 内核 双容差选项。
     * @returns 已按直线 `[0,1]` domain 裁剪的结果。
     */
    public solveSegment(
        line: LineSplineIntersectionLine2D,
        curve: LineSplineCurveDefinition2D,
        options?: LineSplineIntersectionOptions,
    ): LineSplineIntersectionResult2D {
        return this.solve(line, curve, true, options);
    }

    /** 执行公共 Puppy CAD 内核 求交并适配原公开结果。 */
    private solve(
        line: LineSplineIntersectionLine2D,
        curve: LineSplineCurveDefinition2D,
        bounded: boolean,
        options: LineSplineIntersectionOptions | undefined,
    ): LineSplineIntersectionResult2D {
        const adaptor = new ParametricSplineCurveAdaptor2D(curve);
        const result = this.intersector.perform(
            new ImplicitConicTool2D({kind: 'line', start: line.start, end: line.end, bounded}),
            adaptor,
            this.resolveIntersectionOptions(options),
        );
        const intervalBoundaries = adaptor.resolveC1IntervalBoundaries();
        return {
            done: result.done,
            error: result.error,
            points: result.points.map((point) => ({
                point: point.point,
                lineParameter: point.implicitParameter,
                curveParameter: point.curveParameter,
                spanIndex: this.resolveIntervalIndex(intervalBoundaries, point.curveParameter),
                kind: point.implicitTransition.type === 'touch'
                    ? 'tangent'
                    : point.implicitTransition.type === 'undecided'
                        ? 'undetermined'
                        : 'simple',
                atLineEndpoint: point.implicitTransition.position !== 'middle',
                atCurveEndpoint: point.curveTransition.position !== 'middle',
                lineTransition: point.implicitTransition,
                curveTransition: point.curveTransition,
            })),
            overlaps: result.segments.map((segment) => ({
                startPoint: segment.firstEndpoint.point,
                endPoint: segment.secondEndpoint.point,
                startLineParameter: segment.firstEndpoint.implicitParameter,
                endLineParameter: segment.secondEndpoint.implicitParameter,
                startCurveParameter: segment.firstEndpoint.curveParameter,
                endCurveParameter: segment.secondEndpoint.curveParameter,
                spanIndex: this.resolveIntervalIndex(
                    intervalBoundaries,
                    segment.firstEndpoint.curveParameter,
                ),
                direction: segment.opposite ? 'opposite' : 'same',
                firstLineTransition: segment.firstEndpoint.implicitTransition,
                firstCurveTransition: segment.firstEndpoint.curveTransition,
                secondLineTransition: segment.secondEndpoint.implicitTransition,
                secondCurveTransition: segment.secondEndpoint.curveTransition,
            })),
        };
    }

    /** 将兼容选项转换为 Puppy CAD 内核 `TolConf/Tol`。 */
    private resolveIntersectionOptions(
        options: LineSplineIntersectionOptions | undefined,
    ): ImplicitParametricIntersectionOptions | undefined {
        if (!options) return undefined;
        return {
            confusionTolerance: options.confusionTolerance ?? options.distanceTolerance,
            distanceTolerance: options.distanceTolerance,
        };
    }

    /** 根据 C1 区间边界解析稳定复合区间索引。 */
    private resolveIntervalIndex(boundaries: readonly number[], parameter: number): number {
        const index = boundaries.findIndex((_boundary, boundaryIndex) => (
            boundaryIndex < boundaries.length - 1 && parameter <= boundaries[boundaryIndex + 1]! + 1e-10
        ));
        return Math.max(0, index);
    }
}
